import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../lib/constants';
import { ChevronRight, ChevronLeft, User, Clock, Activity, CheckCircle, AlertCircle, FileText, Calendar, Stethoscope, Heart, Thermometer, Droplets, RefreshCw, Loader } from 'lucide-react';
import {
  MdKeyboardArrowRight,
  MdOutlineHealthAndSafety,
  MdOutlineAssignment,
  MdErrorOutline,
  MdAccessTime,
  MdCheckCircle,
  MdOutlineLocalHospital,
  MdOutlineWarning,
  MdOutlineChecklist,
  MdOutlineSafetyCheck,
  MdOutlineMedicalServices,
  MdOutlineDescription,
  MdOutlinePrecisionManufacturing,
  MdEdit,
  MdRadioButtonUnchecked,
  MdOutlineHealing
} from 'react-icons/md';
import { useParams } from 'react-router-dom';
import { useLocation } from 'react-router-dom';

import { useNotification } from '../contexts/NotificationContext';

const SurgicalProcedureSystem = () => {
  const { showNotification } = useNotification();
  const uhid_no = useParams().uhid_no;
  const [loading, setLoading] = useState(true); // Start with loading true
  const [initialLoading, setInitialLoading] = useState(true); // Track initial load
  const location = useLocation();
  const initialStep = location.state?.step ?? 0;
  const [currentStep, setCurrentStep] = useState(initialStep);

  // Enhanced form status tracking for all phases
  const [formStatus, setFormStatus] = useState({
    consideration: {
      status: 'not-started',
      completedForms: 0,
      totalForms: 1,
      forms: {
        considerationPhase: 'not-started'
      }
    },
    preOperative: {
      status: 'not-started',
      completedForms: 0,
      totalForms: 9,
      forms: {
        confirmationPhase: 'not-started',
        consentAnaesthesia: 'not-started',
        preInduction: 'not-started',
        highRiskConsent: 'not-started',
        preOpChecklist: 'not-started',
        safetyChecklist: 'not-started',
        anaestheticEvaluation: 'not-started',
        consentMedical: 'not-started',
        preOperativeOrders: 'not-started'
      }
    },
    intraOperative: {
      status: 'not-started',
      completedForms: 0,
      totalForms: 2,
      forms: {
        intraOperativeNursing: 'not-started',
        operationSheet: 'not-started'
      }
    },
    postOperative: {
      status: 'not-started',
      completedForms: 0,
      totalForms: 5,
      forms: {
        postOperativeOrders: 'not-started',
        postOperativeInstructions: 'not-started',
        postAnaesthesiaRecovery: 'not-started',
        analgesiaAssessment: 'not-started',
        postOperativeHandover: 'not-started'
      }
    }
  });

  // Initial load effect
  useEffect(() => {
    const initializeData = async () => {
      setInitialLoading(true);
      setLoading(true);
      await checkFormStatuses();
      setInitialLoading(false);
    };

    initializeData();
  }, [uhid_no]);

  // Location change effect (for subsequent navigations)
  useEffect(() => {
    if (!initialLoading) {
      checkFormStatuses();
    }
  }, [location, initialLoading]);

  const getFormStatus = (apiResponse, key, section) => {
    if (apiResponse.status === 'fulfilled') {
      const data = apiResponse.value.data;
      if (data && Object.keys(data).length > 0) return 'complete';
    }

    const savedForm = sessionStorage.getItem(`${section}Form_${uhid_no}_${key}`);
    return savedForm ? 'incomplete' : 'not-started';
  };

  const checkFormStatuses = async () => {
    if (!initialLoading) {
      setLoading(true);
    }

    try {
      const results = await Promise.allSettled([
        //consideration phase
        axios.get(`${API_BASE_URL}/get_consideration_phase/${uhid_no}`),
        // Intra-operative
        axios.get(`${API_BASE_URL}/get_intra_op_record/${uhid_no}`),
        axios.get(`${API_BASE_URL}/get_operation_sheet/${uhid_no}`),

        // Pre-operative
        axios.get(`${API_BASE_URL}/get_confirmation_phase/${uhid_no}`),
        axios.get(`${API_BASE_URL}/get_consent/${uhid_no}`),
        axios.get(`${API_BASE_URL}/get_anesthesia_form/${uhid_no}`),
        axios.get(`${API_BASE_URL}/get_consent_high_risk/${uhid_no}`),
        axios.get(`${API_BASE_URL}/get_pre_operative_checklist/${uhid_no}`),
        axios.get(`${API_BASE_URL}/get_surgical_safety/${uhid_no}`),
        axios.get(`${API_BASE_URL}/get_pre_anaesthetic/${uhid_no}`),
        axios.get(`${API_BASE_URL}/get_medical_consent/${uhid_no}`),
        axios.get(`${API_BASE_URL}/get_preop_order/${uhid_no}`),

        // Post-operative
        axios.get(`${API_BASE_URL}/get_post_operative_order/${uhid_no}`),
        axios.get(`${API_BASE_URL}/get_post_op_instruction/${uhid_no}`),
        axios.get(`${API_BASE_URL}/get_alderete_score/${uhid_no}`),
        axios.get(`${API_BASE_URL}/get_assessment/${uhid_no}`),
        axios.get(`${API_BASE_URL}/get_post_op_handover/${uhid_no}`)
      ]);

      // Map statuses
      const considerationForms = {
        considerationPhase: getFormStatus(results[0], 'considerationPhase', 'consideration')
      };
      const intraForms = {
        intraOperativeNursing: getFormStatus(results[1], 'intraOperativeNursing', 'intraOp'),
        operationSheet: getFormStatus(results[2], 'operationSheet', 'intraOp')
      };

      const preOpForms = {
        confirmationPhase: getFormStatus(results[3], 'confirmationPhase', 'preOp'),
        consentAnaesthesia: getFormStatus(results[4], 'consentAnaesthesia', 'preOp'),
        preInduction: getFormStatus(results[5], 'preInduction', 'preOp'),
        highRiskConsent: getFormStatus(results[6], 'highRiskConsent', 'preOp'),
        preOpChecklist: getFormStatus(results[7], 'preOpChecklist', 'preOp'),
        safetyChecklist: getFormStatus(results[8], 'safetyChecklist', 'preOp'),
        anaestheticEvaluation: getFormStatus(results[9], 'anaestheticEvaluation', 'preOp'),
        consentMedical: getFormStatus(results[10], 'consentMedical', 'preOp'),
        preOperativeOrders: getFormStatus(results[11], 'preOperativeOrders', 'preOp')
      };

      const postOpForms = {
        postOperativeOrders: getFormStatus(results[12], 'postOperativeOrders', 'postOp'),
        postOperativeInstructions: getFormStatus(results[13], 'postOperativeInstructions', 'postOp'),
        postAnaesthesiaRecovery: getFormStatus(results[14], 'postAnaesthesiaRecovery', 'postOp'),
        analgesiaAssessment: getFormStatus(results[15], 'analgesiaAssessment', 'postOp'),
        postOperativeHandover: getFormStatus(results[16], 'postOperativeHandover', 'postOp')
      };

      const countComplete = (forms) => Object.values(forms).filter(v => v === 'complete').length;

      setFormStatus(prev => ({
        ...prev,
        consideration: {
          ...prev.consideration,
          forms: considerationForms,
          completedForms: countComplete(considerationForms),
          status: countComplete(considerationForms) === prev.consideration.totalForms ? 'complete' : countComplete(considerationForms) > 0 ? 'incomplete' : 'not-started'
        },
        intraOperative: {
          ...prev.intraOperative,
          forms: intraForms,
          completedForms: countComplete(intraForms),
          status: countComplete(intraForms) === prev.intraOperative.totalForms ? 'complete' : countComplete(intraForms) > 0 ? 'incomplete' : 'not-started'
        },
        preOperative: {
          ...prev.preOperative,
          forms: preOpForms,
          completedForms: countComplete(preOpForms),
          status: countComplete(preOpForms) === prev.preOperative.totalForms ? 'complete' : countComplete(preOpForms) > 0 ? 'incomplete' : 'not-started'
        },
        postOperative: {
          ...prev.postOperative,
          forms: postOpForms,
          completedForms: countComplete(postOpForms),
          status: countComplete(postOpForms) === prev.postOperative.totalForms ? 'complete' : countComplete(postOpForms) > 0 ? 'incomplete' : 'not-started'
        }
      }));

    } catch (error) {
      console.error('Error fetching form statuses:', error);
    } finally {
      setLoading(false);
    }
  };

  const steps = [
    {
      id: 'consideration',
      title: 'Consideration Phase',
      shortTitle: 'Consideration',
      icon: <User className="w-4 h-4 sm:w-5 sm:h-5" />,
      color: 'bg-teal-500',
      description: 'Patient assessment and surgical planning'
    },
    {
      id: 'preOperative',
      title: 'Pre-Operative',
      shortTitle: 'Pre-Op',
      icon: <FileText className="w-4 h-4 sm:w-5 sm:h-5" />,
      color: 'bg-orange-500',
      description: 'Pre-surgery preparation and checks'
    },
    {
      id: 'intraOperative',
      title: 'Intra-Operative',
      shortTitle: 'Intra-Op',
      icon: <Activity className="w-4 h-4 sm:w-5 sm:h-5" />,
      color: 'bg-red-500',
      description: 'During surgery documentation'
    },
    {
      id: 'postOperative',
      title: 'Post-Operative',
      shortTitle: 'Post-Op',
      icon: <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5" />,
      color: 'bg-green-500',
      description: 'Post-surgery care and monitoring'
    }
  ];

  // Show loading screen during initial load
  if (initialLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="flex items-center justify-center mb-4">
            <Loader className="w-8 h-8 text-teal-500 animate-spin mr-3" />
            <div className="text-lg font-semibold text-gray-700">Loading Surgical System</div>
          </div>
          <p className="text-gray-500">Fetching form statuses...</p>
          <div className="mt-4 flex items-center justify-center space-x-2">
            <div className="w-2 h-2 bg-teal-500 rounded-full animate-bounce"></div>
            <div className="w-2 h-2 bg-teal-500 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
            <div className="w-2 h-2 bg-teal-500 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
          </div>
        </div>
      </div>
    );
  }

  // Mock function to refresh all form statuses
  const refreshFormStatuses = async () => {
    setLoading(true);
    try {
      await checkFormStatuses();
      showNotification('success', 'Form statuses refreshed successfully!');
    } catch (error) {
      console.error('Error refreshing form statuses:', error);
      showNotification('error', 'Failed to refresh form statuses. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // Navigation functions
  const nextStep = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const goToStep = (stepIndex) => {
    setCurrentStep(stepIndex);
  };

  // Get overall status based on completion
  const getOverallStatus = (phase) => {
    const { completedForms, totalForms } = formStatus[phase];
    if (completedForms === 0) return 'not-started';
    if (completedForms === totalForms) return 'complete';
    return 'incomplete';
  };

  // Desktop Step Indicator
  const DesktopStepIndicator = () => (
    <div className="hidden lg:flex items-center justify-between mb-8 px-4">
      {steps.map((step, index) => {
        const status = getOverallStatus(step.id);
        return (
          <div key={step.id} className="flex items-center">
            <div className={`flex items-center justify-center w-12 h-12 rounded-full ${status === 'complete' ? 'bg-green-500' :
              status === 'incomplete' ? 'bg-orange-500' :
                index === currentStep ? step.color : 'bg-gray-300'
              } text-white transition-all duration-300 cursor-pointer`}
              onClick={() => goToStep(index)}>
              {status === 'complete' ? (
                <CheckCircle className="w-5 h-5" />
              ) : (
                step.icon
              )}
            </div>
            <div className="ml-3">
              <div className={`font-semibold ${status !== 'not-started' || index === currentStep ? 'text-gray-900' : 'text-gray-500'
                }`}>
                {step.title}
              </div>
              <div className="text-sm text-gray-600">{step.description}</div>
              <div className="text-xs text-gray-500 mt-1">
                {formStatus[step.id].completedForms}/{formStatus[step.id].totalForms} Complete
              </div>
            </div>
            {index < steps.length - 1 && (
              <ChevronRight className="w-6 h-6 text-gray-400 mx-4" />
            )}
          </div>
        );
      })}
    </div>
  );

  // Mobile Step Indicator with enhanced progress info
  const MobileStepIndicator = () => (
    <div className="md:hidden mb-6">
      <div className="bg-white rounded-lg shadow-sm p-4 mb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <div className={`flex items-center justify-center w-8 h-8 rounded-full ${getOverallStatus(steps[currentStep].id) === 'complete' ? 'bg-green-500' :
              getOverallStatus(steps[currentStep].id) === 'incomplete' ? 'bg-orange-500' :
                steps[currentStep].color
              } text-white mr-3`}>
              {getOverallStatus(steps[currentStep].id) === 'complete' ? (
                <CheckCircle className="w-4 h-4" />
              ) : (
                steps[currentStep].icon
              )}
            </div>
            <div>
              <div className="font-semibold text-gray-900 text-sm">
                {steps[currentStep].title}
              </div>
              <div className="text-xs text-gray-600">
                Step {currentStep + 1} of {steps.length}
              </div>
              <div className="text-xs text-gray-500">
                {formStatus[steps[currentStep].id].completedForms}/{formStatus[steps[currentStep].id].totalForms} Forms Complete
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-gray-200 rounded-full h-2 mb-4">
        <div
          className="bg-teal-500 h-2 rounded-full transition-all duration-300"
          style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
        ></div>
      </div>

      <div className="flex justify-between items-center">
        <button
          onClick={prevStep}
          disabled={currentStep === 0}
          className={`flex items-center px-3 py-2 rounded-lg text-sm ${currentStep === 0
            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
            : 'bg-teal-500 text-white hover:bg-teal-600'
            }`}
        >
          <ChevronLeft className="w-4 h-4 mr-1" />
          Previous
        </button>

        <div className="flex space-x-1">
          {steps.map((_, index) => {
            const status = getOverallStatus(steps[index].id);
            return (
              <button
                key={index}
                onClick={() => goToStep(index)}
                className={`w-2 h-2 rounded-full transition-colors ${index === currentStep
                  ? 'bg-teal-500'
                  : status === 'complete'
                    ? 'bg-green-500'
                    : status === 'incomplete'
                      ? 'bg-orange-500'
                      : 'bg-gray-300'
                  }`}
              />
            );
          })}
        </div>

        <button
          onClick={nextStep}
          disabled={currentStep === steps.length - 1}
          className={`flex items-center px-3 py-2 rounded-lg text-sm ${currentStep === steps.length - 1
            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
            : 'bg-teal-500 text-white hover:bg-teal-600'
            }`}
        >
          Next
          <ChevronRight className="w-4 h-4 ml-1" />
        </button>
      </div>
    </div>
  );

  // Consideration Phase Component
  const ConsiderationPhase = () => {
    const status = getOverallStatus('consideration');

    const form = {
      title: 'Consideration Phase',
      description: 'Patient assessment and surgical planning',
      icon: MdOutlineAssignment,
      url: `/considerationPhase/${uhid_no}`
    };

    const IconComponent = form.icon;

    return (
      <div
        onClick={() => (window.location.href = form.url)}
        className={`cursor-pointer p-4 rounded-lg border-2 text-left transition-all duration-200 hover:shadow-md ${status === 'complete'
          ? 'border-green-500 bg-green-50 hover:bg-green-100'
          : status === 'incomplete'
            ? 'border-orange-500 bg-orange-50 hover:bg-orange-100'
            : 'border-gray-300 bg-gray-50 hover:bg-gray-100'
          }`}
      >
        <div className="flex items-start justify-between">
          <div className="flex items-center space-x-3 flex-1">
            <IconComponent
              className={`w-6 h-6 ${status === 'complete'
                ? 'text-green-600'
                : status === 'incomplete'
                  ? 'text-orange-600'
                  : 'text-gray-600'
                }`}
            />
            <div className="flex-1">
              <h3
                className={`font-medium text-sm ${status === 'complete'
                  ? 'text-green-800'
                  : status === 'incomplete'
                    ? 'text-orange-800'
                    : 'text-gray-800'
                  }`}
              >
                {form.title}
              </h3>
              <p className="text-xs text-gray-600 mt-1">{form.description}</p>
              <div className="flex items-center space-x-1 mt-2">
                {status === 'complete' ? (
                  <>
                    <MdCheckCircle className="w-4 h-4 text-green-500" />
                    <span className="text-xs text-green-600">Complete</span>
                  </>
                ) : status === 'incomplete' ? (
                  <>
                    <MdEdit className="w-4 h-4 text-orange-500" />
                    <span className="text-xs text-orange-600">In Progress</span>
                  </>
                ) : (
                  <>
                    <MdRadioButtonUnchecked className="w-4 h-4 text-gray-400" />
                    <span className="text-xs text-gray-600">Not Started</span>
                  </>
                )}
              </div>
            </div>
          </div>
          <MdKeyboardArrowRight className="w-5 h-5 text-gray-400" />
        </div>
      </div>
    );
  };

  // Pre-Operative Phase Component
  const PreOperativePhase = () => {
    const preOpForms = [
      {
        key: 'confirmationPhase',
        icon: MdOutlineAssignment,
        title: 'Confirmation Phase',
        description: 'Final verification and approval',
        url: `/confirmationPhase/${uhid_no}`
      },
      {
        key: 'consentAnaesthesia',
        icon: MdOutlineLocalHospital,
        title: 'Consent for Anaesthesia',
        description: 'Anaesthesia procedure agreement',
        url: `/consentForAnaesthesia/${uhid_no}`
      },
      {
        key: 'preInduction',
        icon: MdOutlineHealthAndSafety,
        title: 'Pre Induction Assessment',
        description: 'Pre-anaesthesia evaluation',
        url: `/preInductionAssessment/${uhid_no}`
      },
      {
        key: 'highRiskConsent',
        icon: MdOutlineWarning,
        title: 'Consent for High Risk',
        description: 'High-risk procedure agreement',
        url: `/highRiskConsentForm/${uhid_no}`
      },
      {
        key: 'preOpChecklist',
        icon: MdOutlineChecklist,
        title: 'Pre Operative Checklist',
        description: 'Pre-surgery verification list',
        url: `/preOperativeChecklist/${uhid_no}`
      },
      {
        key: 'safetyChecklist',
        icon: MdOutlineSafetyCheck,
        title: 'Surgical Safety Checklist',
        description: 'Surgery safety protocols',
        url: `/surgicalSafetyChecklist/${uhid_no}`
      },
      {
        key: 'anaestheticEvaluation',
        icon: MdOutlineMedicalServices,
        title: 'Pre-Anaesthetic Evaluation',
        description: 'Anaesthesia readiness assessment',
        url: `/preAnaestheticForm/${uhid_no}`
      },
      {
        key: 'consentMedical',
        icon: MdOutlineDescription,
        title: 'Consent for Medical/Surgical Procedure',
        description: 'Surgical procedure agreement',
        url: `/consentForMedical/${uhid_no}`
      },
      {
        key: 'preOperativeOrders',
        icon: MdOutlinePrecisionManufacturing,
        title: 'Pre Operative Orders',
        description: 'Orders for pre-operative care',
        url: `/preoperativeorders/${uhid_no}`
      }
    ];

    return (
      <div className="space-y-4">
        <div className="bg-blue-50 p-4 rounded-lg mb-6">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-medium text-gray-700">Pre-Operative Progress</span>
            <span className="text-sm text-gray-600">
              {formStatus.preOperative.completedForms}/{formStatus.preOperative.totalForms} Complete
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-orange-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${(formStatus.preOperative.completedForms / formStatus.preOperative.totalForms) * 100}%` }}
            ></div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {preOpForms.map(form => {
            const status = formStatus.preOperative.forms[form.key];
            const IconComponent = form.icon;

            return (
              <button
                key={form.key}
                onClick={() => window.location.href = form.url}
                className={`p-4 rounded-lg border-2 text-left transition-all duration-200 hover:shadow-md ${status === 'complete'
                  ? 'border-green-500 bg-green-50 hover:bg-green-100'
                  : status === 'incomplete'
                    ? 'border-orange-500 bg-orange-50 hover:bg-orange-100'
                    : 'border-gray-300 bg-gray-50 hover:bg-gray-100'
                  }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-center space-x-3 flex-1">
                    <IconComponent className={`w-6 h-6 ${status === 'complete' ? 'text-green-600' :
                      status === 'incomplete' ? 'text-orange-600' : 'text-gray-600'
                      }`} />
                    <div className="flex-1">
                      <h3 className={`font-medium text-sm ${status === 'complete' ? 'text-green-800' :
                        status === 'incomplete' ? 'text-orange-800' : 'text-gray-800'
                        }`}>
                        {form.title}
                      </h3>
                      <p className="text-xs text-gray-600 mt-1">{form.description}</p>
                      <div className="flex items-center space-x-1 mt-2">
                        {status === 'complete' ? (
                          <><MdCheckCircle className="w-4 h-4 text-green-500" /> <span className="text-xs text-green-600">Complete</span></>
                        ) : status === 'incomplete' ? (
                          <><MdEdit className="w-4 h-4 text-orange-500" /> <span className="text-xs text-orange-600">In Progress</span></>
                        ) : (
                          <><MdRadioButtonUnchecked className="w-4 h-4 text-gray-400" /> <span className="text-xs text-gray-600">Not Started</span></>
                        )}
                      </div>
                    </div>
                  </div>
                  <MdKeyboardArrowRight className="w-5 h-5 text-gray-400" />
                </div>
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  // Intra-Operative Phase Component
  const IntraOperativePhase = () => {
    const intraOpForms = [
      {
        key: 'intraOperativeNursing',
        title: 'Intra Operative Nursing Record',
        description: 'Nursing documentation during surgery',
        icon: MdOutlineAssignment,
        url: `/intraOperativeNursingRecord/${uhid_no}`
      },
      {
        key: 'operationSheet',
        title: 'Operation Sheet',
        description: 'Surgical procedure documentation',
        icon: MdOutlineDescription,
        url: `/operationSheet/${uhid_no}`
      }
    ];

    return (
      <div className="space-y-4">
        <div className="bg-red-50 p-4 rounded-lg mb-6">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-medium text-gray-700">Intra-Operative Progress</span>
            <span className="text-sm text-gray-600">
              {formStatus.intraOperative.completedForms}/{formStatus.intraOperative.totalForms} Complete
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-red-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${(formStatus.intraOperative.completedForms / formStatus.intraOperative.totalForms) * 100}%` }}
            ></div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4">
          {intraOpForms.map(form => {
            const status = formStatus.intraOperative.forms[form.key];
            const IconComponent = form.icon;

            return (
              <button
                key={form.key}
                onClick={() => window.location.href = form.url}
                className={`p-6 rounded-lg border-2 text-left transition-all duration-200 hover:shadow-md ${status === 'complete'
                  ? 'border-green-500 bg-green-50 hover:bg-green-100'
                  : status === 'incomplete'
                    ? 'border-orange-500 bg-orange-50 hover:bg-orange-100'
                    : 'border-gray-300 bg-gray-50 hover:bg-gray-100'
                  }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <IconComponent className={`w-8 h-8 ${status === 'complete' ? 'text-green-600' :
                      status === 'incomplete' ? 'text-orange-600' : 'text-gray-600'
                      }`} />
                    <div>
                      <h3 className={`font-medium ${status === 'complete' ? 'text-green-800' :
                        status === 'incomplete' ? 'text-orange-800' : 'text-gray-800'
                        }`}>
                        {form.title}
                      </h3>
                      <p className="text-sm text-gray-600 mt-1">{form.description}</p>
                      <div className="flex items-center space-x-1 mt-2">
                        {status === 'complete' ? (
                          <><MdCheckCircle className="w-4 h-4 text-green-500" /> <span className="text-sm text-green-600">Complete</span></>
                        ) : status === 'incomplete' ? (
                          <><MdEdit className="w-4 h-4 text-orange-500" /> <span className="text-sm text-orange-600">In Progress</span></>
                        ) : (
                          <><MdRadioButtonUnchecked className="w-4 h-4 text-gray-400" /> <span className="text-sm text-gray-600">Not Started</span></>
                        )}
                      </div>
                    </div>
                  </div>
                  <MdKeyboardArrowRight className="w-6 h-6 text-gray-400" />
                </div>
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  // Post-Operative Phase Component
  const PostOperativePhase = () => {
    const postOpForms = [
      {
        key: 'postOperativeOrders',
        icon: MdOutlineAssignment,
        title: 'Post Operative Orders',
        description: 'Post-surgery medical instructions',
        url: `/intraOperativeOrders/${uhid_no}`
      },
      {
        key: 'postOperativeInstructions',
        icon: MdOutlineDescription,
        title: 'Post Operative Instructions',
        description: 'Patient care guidelines',
        url: `/postOperativeOrders/${uhid_no}`
      },
      {
        key: 'postAnaesthesiaRecovery',
        icon: MdOutlineLocalHospital,
        title: 'Post Anaesthesia Recovery Score',
        description: 'Recovery monitoring assessment',
        url: `/AldreteScore/${uhid_no}`
      },
      {
        key: 'analgesiaAssessment',
        icon: MdOutlineMedicalServices,
        title: 'Analgesia Assessment Chart',
        description: 'Pain management tracking',
        url: `/postOperativeAssesment/${uhid_no}`
      },
      {
        key: 'postOperativeHandover',
        icon: MdOutlineHealthAndSafety,
        title: 'Post Operative Handover',
        description: 'Post-surgery evaluation',
        url: `/postOperativeForm2/${uhid_no}`
      }
    ];

    return (
      <div className="space-y-4">
        <div className="bg-green-50 p-4 rounded-lg mb-6">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-medium text-gray-700">Post-Operative Progress</span>
            <span className="text-sm text-gray-600">
              {formStatus.postOperative.completedForms}/{formStatus.postOperative.totalForms} Complete
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-green-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${(formStatus.postOperative.completedForms / formStatus.postOperative.totalForms) * 100}%` }}
            ></div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {postOpForms.map(form => {
            const status = formStatus.postOperative.forms[form.key];
            const IconComponent = form.icon;

            return (
              <button
                key={form.key}
                onClick={() => window.location.href = form.url}
                className={`p-4 rounded-lg border-2 text-left transition-all duration-200 hover:shadow-md ${status === 'complete'
                  ? 'border-green-500 bg-green-50 hover:bg-green-100'
                  : status === 'incomplete'
                    ? 'border-orange-500 bg-orange-50 hover:bg-orange-100'
                    : 'border-gray-300 bg-gray-50 hover:bg-gray-100'
                  }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-center space-x-3 flex-1">
                    <IconComponent className={`w-6 h-6 ${status === 'complete' ? 'text-green-600' :
                      status === 'incomplete' ? 'text-orange-600' : 'text-gray-600'
                      }`} />
                    <div className="flex-1">
                      <h3 className={`font-medium text-sm ${status === 'complete' ? 'text-green-800' :
                        status === 'incomplete' ? 'text-orange-800' : 'text-gray-800'
                        }`}>
                        {form.title}
                      </h3>
                      <p className="text-xs text-gray-600 mt-1">{form.description}</p>
                      <div className="flex items-center space-x-1 mt-2">
                        {status === 'complete' ? (
                          <><MdCheckCircle className="w-4 h-4 text-green-500" /> <span className="text-xs text-green-600">Complete</span></>
                        ) : status === 'incomplete' ? (
                          <><MdEdit className="w-4 h-4 text-orange-500" /> <span className="text-xs text-orange-600">In Progress</span></>
                        ) : (
                          <><MdRadioButtonUnchecked className="w-4 h-4 text-gray-400" /> <span className="text-xs text-gray-600">Not Started</span></>
                        )}
                      </div>
                    </div>
                  </div>
                  <MdKeyboardArrowRight className="w-5 h-5 text-gray-400" />
                </div>
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 0:
        return <ConsiderationPhase />;
      case 1:
        return <PreOperativePhase />;
      case 2:
        return <IntraOperativePhase />;
      case 3:
        return <PostOperativePhase />;
      default:
        return <ConsiderationPhase />;
    }
  };

  // Calculate overall progress
  const totalCompletedForms = Object.values(formStatus).reduce((sum, phase) => sum + phase.completedForms, 0);
  const totalForms = Object.values(formStatus).reduce((sum, phase) => sum + phase.totalForms, 0);

  return (
    <div className="min-h-screen bg-gray-50 py-4 sm:py-8">
      <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8">
        {/* Header */}
        <div className="text-center mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-2">
            Surgical Procedure Management System
          </h1>
          <p className="text-base sm:text-lg lg:text-xl text-gray-600 px-2">
            Comprehensive documentation system for all surgical phases
          </p>
          <div className="mt-4 bg-white rounded-lg shadow-sm p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">Overall System Progress</span>
              <div className="flex items-center space-x-3">
                <span className="text-sm text-gray-600">
                  UHID: {uhid_no} | {totalCompletedForms}/{totalForms} Forms Complete
                </span>
                {loading && !initialLoading && (
                  <div className="flex items-center text-xs text-teal-600">
                    <Loader className="w-3 h-3 animate-spin mr-1" />
                    Updating...
                  </div>
                )}
              </div>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div
                className="bg-gradient-to-r from-teal-500 to-green-500 h-3 rounded-full transition-all duration-500"
                style={{ width: `${totalForms > 0 ? (totalCompletedForms / totalForms) * 100 : 0}%` }}
              ></div>
            </div>
            <div className="text-xs text-gray-500 mt-1 text-center">
              {totalForms > 0 ? Math.round((totalCompletedForms / totalForms) * 100) : 0}% Complete
            </div>
          </div>
        </div>

        {/* Step Indicators */}
        <DesktopStepIndicator />
        <MobileStepIndicator />

        {/* Main Content */}
        <div className="bg-white rounded-lg sm:rounded-xl shadow-lg overflow-hidden">
          {loading && !initialLoading ? (
            <div className="p-8 text-center">
              <div className="flex items-center justify-center mb-4">
                <Loader className="w-6 h-6 text-teal-500 animate-spin mr-3" />
                <div className="text-lg font-semibold text-gray-700">Refreshing form statuses...</div>
              </div>
            </div>
          ) : (
            <div className="p-4 sm:p-6 lg:p-8">
              {renderCurrentStep()}
            </div>
          )}

          {/* Desktop Navigation Buttons */}
          <div className="hidden md:flex justify-between items-center p-4 sm:p-6 bg-gray-50 border-t">
            <button
              onClick={prevStep}
              disabled={currentStep === 0}
              className={`flex items-center px-4 sm:px-6 py-2 sm:py-3 rounded-lg font-medium transition-colors ${currentStep === 0
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
            >
              <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
              Previous Step
            </button>

            <div className="flex items-center space-x-4">
              <button
                onClick={refreshFormStatuses}
                disabled={loading}
                className="flex items-center px-4 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                {loading ? 'Refreshing...' : 'Refresh Status'}
              </button>

              <div className="flex space-x-2">
                {steps.map((_, index) => {
                  const status = getOverallStatus(steps[index].id);
                  return (
                    <button
                      key={index}
                      onClick={() => goToStep(index)}
                      className={`w-3 h-3 rounded-full transition-colors ${index === currentStep
                        ? 'bg-teal-500'
                        : status === 'complete'
                          ? 'bg-green-500'
                          : status === 'incomplete'
                            ? 'bg-orange-500'
                            : 'bg-gray-300'
                        }`}
                    />
                  );
                })}
              </div>
            </div>

            <button
              onClick={nextStep}
              disabled={currentStep === steps.length - 1}
              className={`flex items-center px-4 sm:px-6 py-2 sm:py-3 rounded-lg font-medium transition-colors ${currentStep === steps.length - 1
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-teal-500 text-white hover:bg-teal-600'
                }`}
            >
              Next Step
              <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 ml-2" />
            </button>
          </div>
        </div>

        {/* Enhanced Progress Summary */}
        <div className="mt-6 sm:mt-8 bg-white rounded-lg shadow p-4 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900">Phase Overview</h3>
            <button
              onClick={refreshFormStatuses}
              disabled={loading}
              className="text-sm text-teal-600 hover:text-teal-800 flex items-center space-x-1 disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              <span>Refresh</span>
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4">
            {steps.map((step, index) => {
              const status = getOverallStatus(step.id);
              const phaseData = formStatus[step.id];

              return (
                <div
                  key={step.id}
                  className={`p-3 sm:p-4 rounded-lg border-2 cursor-pointer transition-all hover:shadow-md ${index === currentStep
                    ? 'border-teal-500 bg-teal-50 shadow-md'
                    : status === 'complete'
                      ? 'border-green-500 bg-green-50'
                      : status === 'incomplete'
                        ? 'border-orange-500 bg-orange-50'
                        : 'border-gray-200 bg-gray-50'
                    }`}
                  onClick={() => goToStep(index)}
                >
                  <div className="flex items-center mb-3">
                    <div className={`w-6 h-6 sm:w-8 sm:h-8 rounded-full flex items-center justify-center ${index === currentStep
                      ? 'bg-teal-500 text-white ring-4 ring-teal-100'
                      : status === 'complete'
                        ? 'bg-green-500 text-white'
                        : status === 'incomplete'
                          ? 'bg-orange-500 text-white'
                          : 'bg-gray-300 text-gray-600'
                      }`}>
                      {status === 'complete' ? (
                        <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4" />
                      ) : (
                        React.cloneElement(step.icon, { className: 'w-3 h-3 sm:w-4 sm:h-4' })
                      )}
                    </div>
                    <div className="ml-2 sm:ml-3 min-w-0 flex-1">
                      <div className={`font-medium text-xs sm:text-sm ${index === currentStep
                        ? 'text-teal-900'
                        : status === 'complete'
                          ? 'text-green-900'
                          : status === 'incomplete'
                            ? 'text-orange-900'
                            : 'text-gray-700'
                        } truncate`}>
                        {window.innerWidth < 640 ? step.shortTitle : step.title}
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-600">Progress</span>
                      <span className="font-medium">
                        {phaseData.completedForms}/{phaseData.totalForms}
                      </span>
                    </div>

                    <div className="w-full bg-gray-200 rounded-full h-1.5">
                      <div
                        className={`h-1.5 rounded-full transition-all duration-300 ${status === 'complete'
                          ? 'bg-green-500'
                          : status === 'incomplete'
                            ? 'bg-orange-500'
                            : 'bg-gray-300'
                          }`}
                        style={{ width: `${phaseData.totalForms > 0 ? (phaseData.completedForms / phaseData.totalForms) * 100 : 0}%` }}
                      ></div>
                    </div>

                    <div className={`text-xs font-medium ${index === currentStep
                      ? 'text-teal-700'
                      : status === 'complete'
                        ? 'text-green-700'
                        : status === 'incomplete'
                          ? 'text-orange-700'
                          : 'text-gray-600'
                      }`}>
                      {index === currentStep
                        ? 'Currently viewing'
                        : status === 'complete'
                          ? 'Phase completed'
                          : status === 'incomplete'
                            ? 'In progress'
                            : 'Not started'
                      }
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="mt-4 sm:mt-6 flex flex-wrap gap-2 sm:gap-4 justify-center">
          <button
            onClick={() => window.location.href = `/home`}
            className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded">
            Back to Home
          </button>
          <button
            onClick={() => window.location.href = `/addfile/${uhid_no}`}
            className="flex items-center px-3 sm:px-4 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors text-sm"
          >
            <FileText className="w-4 h-4 mr-2" />
            ADD FILES
          </button>

          <button
            onClick={() => {
              const completedPhases = steps.filter((step, index) => getOverallStatus(step.id) === 'complete').length;
              showNotification('info', `Surgery Progress: ${completedPhases}/${steps.length} phases completed, ${totalCompletedForms}/${totalForms} total forms completed. Overall: ${Math.round((totalCompletedForms / totalForms) * 100)}%`);
            }}
            className="flex items-center px-3 sm:px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-sm"
          >
            <Activity className="w-4 h-4 mr-2" />
            PROGRESS REPORT
          </button>

          <button
            onClick={() => window.print()}
            className="flex items-center px-3 sm:px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors text-sm"
          >
            <FileText className="w-4 h-4 mr-2" />
            PRINT SUMMARY
          </button>
        </div>

        {/* Status Legend */}
        <div className="mt-4 bg-gray-100 rounded-lg p-4">
          <h4 className="text-sm font-medium text-gray-700 mb-3">Status Guide:</h4>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-green-500 rounded-full"></div>
              <span className="text-gray-600">Completed - All forms submitted</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-orange-500 rounded-full"></div>
              <span className="text-gray-600">In Progress - Some forms completed</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-gray-400 rounded-full"></div>
              <span className="text-gray-600">Not Started - No forms completed</span>
            </div>
          </div>
          <div className="mt-3 pt-3 border-t border-gray-300">
            <p className="text-xs text-gray-600">
              💡 <strong>Tip:</strong> After completing forms, you'll return to this dashboard automatically.
              Use the "Refresh Status" button to update completion status if needed.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SurgicalProcedureSystem;